/**
 * CircularGauge — the "meridian gauge" (Claude Design v4, signature 1d).
 *
 * A 270° arc with the opening centred at the BOTTOM, butt caps, and the
 * label seated in that opening. Sweep, cap style and stroke thickness are
 * brand tokens read from the cascade, so a brand can revert to a full
 * round-cap ring without touching code:
 *   --gauge-sweep: 360;  --gauge-caps: round;  --gauge-thickness: 4px;
 *
 * Track colour comes from `--stat-bar-bg` (NEUTRAL). The progress colour is
 * caller-set: the `--team-home`/`--team-away` pair when comparing two sides,
 * `--accent-primary` for a single subject. NEVER status colours — a 38%
 * gauge is not a "loss". See components/widget-color-sources.md.
 *
 * Empty state → em-dash, no progressbar `aria-valuenow`.
 *
 * Shape: { value: number | null; color: string; size?: number }
 */

import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { z } from 'zod';
import type { PrimitiveMeta } from './_meta.js';
import styles from './CircularGauge.module.css';

const dataShape = z.object({
  /** 0..1 ratio. null → empty state (em-dash). */
  value: z.number().min(0).max(1).nullable(),
  /** Hex / CSS colour for the filled arc. */
  color: z.string(),
  /** Diameter in CSS px. Default 56. */
  size: z.number().int().positive().optional(),
});

export const META: PrimitiveMeta = {
  name: 'circular-gauge',
  description: '270° meridian arc with centred % readout (or em-dash).',
  composable: true,
  dataShape,
  densities: ['compact', 'standard'],
  breakpoints: { mobile: true, wide: true },
  animation: 'reveal',
};

interface CircularGaugeProps {
  /** 0..1 ratio. null → empty state with em-dash. */
  value: number | null;
  /** Hex / CSS colour for the filled arc. */
  color: string;
  /** Diameter in CSS px. Default 56. */
  size?: number;
}

const DEFAULT_THICKNESS = 8;
const DEFAULT_SWEEP = 270;
const DEFAULT_CAPS: 'butt' | 'round' = 'butt';

/**
 * Compact-by-default law (2026-07-31): no gauge renders larger than this,
 * regardless of what a caller (including the layout LLM) asks for. A
 * percentage is one number — spending >64px of diameter on it is screen
 * waste. Callers wanting more emphasis should switch representation
 * (stat-row share bar), not inflate the gauge.
 */
const MAX_SIZE = 64;

/** Read a numeric brand token off :root, falling back when unset/unparseable. */
function readNumericToken(name: string, fallback: number): number {
  if (typeof window === 'undefined') return fallback;
  const raw = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  if (raw.length === 0) return fallback;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function readCapsToken(): 'butt' | 'round' {
  if (typeof window === 'undefined') return DEFAULT_CAPS;
  const raw = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue('--gauge-caps')
    .trim();
  return raw === 'round' ? 'round' : DEFAULT_CAPS;
}

export function CircularGauge({
  value,
  color,
  size: requestedSize = 56,
}: CircularGaugeProps): ReactElement {
  const size = Math.min(requestedSize, MAX_SIZE);
  // Tokens are read after mount so SSR/first paint use the platform
  // defaults and the brand cascade refines them — no layout thrash
  // because stroke width does not affect the box.
  const [strokeWidth, setStrokeWidth] = useState(DEFAULT_THICKNESS);
  const [sweepDeg, setSweepDeg] = useState(DEFAULT_SWEEP);
  const [caps, setCaps] = useState<'butt' | 'round'>(DEFAULT_CAPS);
  const mounted = useRef(false);

  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;
    setStrokeWidth(readNumericToken('--gauge-thickness', DEFAULT_THICKNESS));
    const sweep = readNumericToken('--gauge-sweep', DEFAULT_SWEEP);
    setSweepDeg(Math.min(360, Math.max(90, sweep)));
    setCaps(readCapsToken());
  }, []);

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const isEmpty = value === null;
  const safeValue = isEmpty ? 0 : Math.max(0, Math.min(1, value));

  // Fraction of the full circle the arc spans (0.75 at 270°).
  const sweepFraction = sweepDeg / 360;
  const arcLength = circumference * sweepFraction;
  // Rotate so the arc's GAP is centred at the bottom: start the arc at
  // 90° + half the missing sweep, measured from the 12 o'clock origin
  // that `rotate(-90 …)` establishes.
  const gapDeg = 360 - sweepDeg;
  const rotation = -90 + gapDeg / 2;

  const progressLength = arcLength * safeValue;

  return (
    <div
      className={styles.gauge}
      style={{ width: `${size}px`, height: `${size}px` }}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      {...(!isEmpty ? { 'aria-valuenow': Math.round(safeValue * 100) } : {})}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className={styles.gaugeSvg}
        aria-hidden="true"
      >
        {/* Paint goes through `style` — SVG presentation ATTRIBUTES do not
            resolve `var()`, and both the track token and the caller-set
            colour are var() strings since ADR-063. */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          style={{ stroke: 'var(--stat-bar-bg, #EDF0F5)' }}
          strokeWidth={strokeWidth}
          strokeLinecap={caps}
          strokeDasharray={`${arcLength} ${circumference}`}
          transform={`rotate(${rotation} ${size / 2} ${size / 2})`}
        />
        {!isEmpty && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth={strokeWidth}
            strokeLinecap={caps}
            strokeDasharray={`${progressLength} ${circumference}`}
            transform={`rotate(${rotation} ${size / 2} ${size / 2})`}
            style={{
              stroke: color,
              transition:
                'stroke-dasharray var(--motion-standard, 200ms) var(--motion-easing, ease)',
            }}
          />
        )}
      </svg>
      <span className={styles.gaugeText}>
        {isEmpty ? (
          <span className={styles.gaugeDash}>—</span>
        ) : (
          <>
            <span className={styles.gaugeValue}>{Math.round(safeValue * 100)}</span>
            <span className={styles.gaugeUnit}>%</span>
          </>
        )}
      </span>
    </div>
  );
}

export default CircularGauge;
